import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ContextService } from '@bcef/ia-shared';
import { Observable } from 'rxjs';

import {
  AdditionnalFieldsKeys,
  AdditionnalFieldsLabels,
  PersonalInformation,
  TerminationInformation,
  TerminationRequest,
  TerminationRequestValues,
} from '@features/termination/interfaces';
import { sanitizeString } from '@shared/utils/not-allowed-characters';

import { ChannelType, CONTRACT_TYPE, ContractType } from '../enums';
import { Product } from '../models';

@Injectable({
  providedIn: 'root',
})
export class TerminationRequestService {
  private readonly http = inject(HttpClient);

  private readonly contextService = inject(ContextService);

  public sendRequest(terminationRequest: TerminationRequest): Observable<void> {
    return this.http.post<void>('/rgg/demands/v1/demands/customer-demand', terminationRequest);
  }

  public payloadRequestMapper(
    personalInformation: PersonalInformation,
    terminationInformation: TerminationInformation,
    insurances: Product[],
    bankingProducts: Product[],
  ): TerminationRequest {
    const request: TerminationRequest = {
      customerStatus: TerminationRequestValues.supported,
      collaboratorStatus: TerminationRequestValues.supported,
      brand: TerminationRequestValues.brand,
      beneficiaryType: 0,
      applicantId: TerminationRequestValues.empty,
      isToReview: false,
      reviewDate: TerminationRequestValues.empty,
      responsibleCollaboratorId: TerminationRequestValues.notSpecified,
      collaboratorFullName: TerminationRequestValues.notSpecified,
      responsibleType: 0,
      creationDate: new Date().toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
      treatmentEndDate: TerminationRequestValues.empty,
      media: this.contextService.media,
      applicant: 'Client',
      origin: 'Client',
      channel: this.contextService.canal,
      reason: {
        functionalCode:
          terminationInformation.contractType === ContractType.INSURANCE ? 'A511' : 'A510',
      },
      auditTrails: [
        {
          action: TerminationRequestValues.action,
          actionDate: new Date().toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
          operatorId: TerminationRequestValues.operatorId,
          description: TerminationRequestValues.description,
        },
      ],
      state: {
        id: 2,
      },
      family: {
        domain: TerminationRequestValues.domain,
      },
      creator: TerminationRequestValues.creator,
      additionnalFields: [
        {
          key: AdditionnalFieldsKeys.NOM_DEMANDEUR,
          value: personalInformation.applicantLastName,
          label: AdditionnalFieldsLabels.NOM_DEMANDEUR,
          displayOrder: 1,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.PRENOM_DEMANDEUR,
          value: personalInformation.applicantFirstName,
          label: AdditionnalFieldsLabels.PRENOM_DEMANDEUR,
          displayOrder: 2,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.INDICATEUR_AR_MAIL,
          value: personalInformation.channelType === ChannelType.EMAIL ? true : false,
          label: AdditionnalFieldsLabels.INDICATEUR_AR_MAIL,
          displayOrder: 3,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.CLIENT_ADRESSE_MAIL,
          value: personalInformation.email,
          label: AdditionnalFieldsLabels.CLIENT_ADRESSE_MAIL,
          displayOrder: 4,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.INDICATEUR_AR_PAPIER,
          value: personalInformation.channelType === ChannelType.PAPER ? true : false,
          label: AdditionnalFieldsLabels.INDICATEUR_AR_PAPIER,
          displayOrder: 5,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.ADRESSE,
          value: personalInformation.address,
          label: AdditionnalFieldsLabels.ADRESSE,
          displayOrder: 6,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.CODE_POSTAL,
          value: personalInformation.postalCode,
          label: AdditionnalFieldsLabels.CODE_POSTAL,
          displayOrder: 7,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.VILLE,
          value: personalInformation.city,
          label: AdditionnalFieldsLabels.VILLE,
          displayOrder: 8,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.TYPE_CONTRAT,
          value: CONTRACT_TYPE[terminationInformation.contractType as ContractType],
          label: AdditionnalFieldsLabels.TYPE_CONTRAT,
          displayOrder: 17,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.TYPE_CONTRAT,
          value: CONTRACT_TYPE[terminationInformation.contractType as ContractType],
          label: AdditionnalFieldsLabels.TYPE_CONTRAT,
          displayOrder: 17,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.TYPE_DEMANDE,
          value: terminationInformation.terminationType,
          label: AdditionnalFieldsLabels.TYPE_DEMANDE,
          displayOrder: 18,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.NOM_CONTRAT,
          value: sanitizeString(
            this.getFormattedContractName(
              terminationInformation.contract,
              terminationInformation.contractName,
            ),
          ),
          label: AdditionnalFieldsLabels.NOM_CONTRAT,
          displayOrder: 20,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.NUMERO_CONTRAT,
          value: terminationInformation.contractNumber,
          label: AdditionnalFieldsLabels.NUMERO_CONTRAT,
          displayOrder: 21,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.INDICATEUR_PRODUIT_ADE,
          value: this.getIsADEProduct(
            terminationInformation.contractType,
            terminationInformation.contract,
            insurances,
            bankingProducts,
          ),
          label: AdditionnalFieldsLabels.INDICATEUR_PRODUIT_ADE,
          displayOrder: 22,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.DATE_RESILIATION_SOUHAITEE,
          value:
            terminationInformation.contractType === ContractType.INSURANCE
              ? new Date(terminationInformation.terminationDate).toLocaleDateString('fr-FR')
              : terminationInformation.terminationDate,
          label: AdditionnalFieldsLabels.DATE_RESILIATION_SOUHAITEE,
          displayOrder: 23,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.MOTIF_RESILIATION,
          value: terminationInformation.terminationReason,
          label: AdditionnalFieldsLabels.MOTIF_RESILIATION,
          displayOrder: 24,
          bloc: 1,
        },
        {
          key: AdditionnalFieldsKeys.AUTRE_MOTIF,
          value: terminationInformation.otherReason,
          label: AdditionnalFieldsLabels.AUTRE_MOTIF,
          displayOrder: 25,
          bloc: 1,
        },
      ],
    };
    return request;
  }

  private getFormattedContractName(contractName: string, otherContractName: string): string {
    return contractName === 'Autre' ? `Autre / ${otherContractName ?? ''}`.trim() : contractName;
  }

  private getIsADEProduct(
    contractType: string,
    productName: string,
    insurances: Product[],
    bankingProducts: Product[],
  ): boolean {
    const products = contractType === ContractType.BANKING_PRODUCT ? bankingProducts : insurances;
    return products.find((p) => p.productName === productName)?.isADEProduct ?? false;
  }
}
